/**
 * SSR renderer for /{locale}/pcc/services/$service (84 pages total).
 * Emits self-referencing canonical, hreflang cluster (th + 6 locales +
 * x-default), FAQPage + Service + Offer + HowTo + BreadcrumbList JSON-LD,
 * and unique H1/lead/H2s from the locale pool.
 */
import { quotePhrase } from "@/config/pricing";
import { Link } from "@tanstack/react-router";
import type { PccService } from "@/data/pcc";
import type { PccLocale } from "@/data/pcc/locale-copy";
import { PCC_LOCALE_COPY } from "@/data/pcc/locale-copy";
import {
  buildPccLocaleServiceContent,
  pccLocaleServiceLabels,
} from "@/lib/pcc/locale-service-builder";
import { OfficialFeeNote } from "@/components/OfficialFeeNote";
import { ContentFreshness } from "@/components/ContentFreshness";
import { RelatedClusters } from "@/components/RelatedClusters";

import { SITE, absoluteUrl } from "@/config/sites";
import { JsonLd } from "@/components/JsonLd";

export function PccLocaleServicePage({
  locale,
  service,
}: {
  locale: PccLocale;
  service: PccService;
}) {
  const copy = PCC_LOCALE_COPY[locale];
  const labels = pccLocaleServiceLabels(locale);
  const content = buildPccLocaleServiceContent(locale, service);
  const url = absoluteUrl(`/${locale}/pcc/services/${service.slug}`);
  const orgId = SITE.canonicalHost ? `https://${SITE.canonicalHost}/#org` : "/#org";

  const graph = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LegalService",
        "@id": orgId,
        name: SITE.name,
        areaServed: ["TH", "AU", "NZ", "UK", "US", "CA", "SG", "JP", "KR", "CN", "DE"],
        additionalType: "https://schema.org/GovernmentService",
      },
      {
        "@type": "WebPage",
        "@id": url,
        url,
        name: content.h1,
        description: content.lead.slice(0, 300),
        inLanguage: copy.hreflang,
        isPartOf: { "@id": orgId },
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: ["h1", ".speakable-summary", ".lead", "[data-speakable]"],
        },
      },
      {
        "@type": "Service",
        name: `Police Clearance Certificate — ${service.nameEn}`,
        serviceType: "Government liaison / Document procurement",
        provider: { "@id": orgId },
        areaServed: "Thailand",
        audience: service.audience.join(", "),
        offers: {
          "@type": "Offer",
          availability: "https://schema.org/InStock",
          deliveryLeadTime: {
            "@type": "QuantitativeValue",
            value: service.turnaroundDays,
            unitText: "business days",
          },
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: content.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "HowTo",
        name: `How to order ${service.nameEn}`,
        step: content.steps.map((s, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: s.tag,
          text: s.text,
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: copy.breadcrumbHome,
            item: absoluteUrl(`/${locale}`),
          },
          {
            "@type": "ListItem",
            position: 2,
            name: copy.breadcrumbPcc,
            item: absoluteUrl(`/${locale}/pcc`),
          },
          { "@type": "ListItem", position: 3, name: service.nameEn, item: url },
        ],
      },
    ],
  };

  return (
    <>
      <JsonLd data={graph} />
      <article
        className="mx-auto max-w-4xl px-4 py-10 prose prose-slate max-w-none"
        lang={copy.hreflang}
      >
        <nav className="text-sm text-slate-500 mb-4" aria-label="Breadcrumb">
          <Link to="/">{copy.breadcrumbHome}</Link>
          {" › "}
          <Link to={`/${locale}/pcc` as any}>{copy.breadcrumbPcc}</Link>
          {" › "}
          <span>{service.nameEn}</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900">{content.h1}</h1>
        <p className="speakable-summary lead answer-summary text-lg text-slate-700">
          {content.lead}
        </p>

        <div className="grid gap-3 md:grid-cols-3 my-6">
          <div className="rounded border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs uppercase text-slate-500">{labels.price}</div>
            <div className="text-base font-semibold">{quotePhrase(locale)}</div>
          </div>
          <div className="rounded border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs uppercase text-slate-500">{labels.days}</div>
            <div className="text-base font-semibold">{labels.daysValue}</div>
          </div>
          <div className="rounded border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs uppercase text-slate-500">{labels.category}</div>
            <div className="text-xl font-bold capitalize">{service.category}</div>
          </div>
        </div>

        {content.sections.map((sec, i) => (
          <section key={i} aria-label={sec.heading}>
            <h2>{sec.heading}</h2>
            <div dangerouslySetInnerHTML={{ __html: sec.html }} />
          </section>
        ))}

        <section aria-labelledby="fields">
          <h2 id="fields">{labels.fields.replaceAll("${name}", service.nameEn)}</h2>
          <ul>
            {service.includes.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="accepted">
          <h2 id="accepted">{labels.accepted}</h2>
          <p className="text-sm">{labels.acceptedNote}</p>
          <ul>
            {service.acceptedBy.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="steps">
          <h2 id="steps">{labels.steps}</h2>
          <ol className="space-y-3">
            {content.steps.map((s, i) => (
              <li key={i} className="rounded border-l-4 border-blue-600 bg-blue-50 p-3">
                <strong className="text-blue-900">
                  {i + 1}. {s.tag} —
                </strong>{" "}
                <span className="text-slate-800">{s.text}</span>
              </li>
            ))}
          </ol>
        </section>

        <OfficialFeeNote keys={["policeClearance", "mfaLegalisation"]} locale={locale} />

        <section
          aria-labelledby="faq"
          style={{ contentVisibility: "auto", containIntrinsicSize: "600px 800px" }}
        >
          <h2 id="faq">{labels.faq}</h2>
          <div className="space-y-4">
            {content.faqs.map((f, i) => (
              <details key={i} className="rounded border border-slate-200 p-4">
                <summary className="font-semibold cursor-pointer text-slate-900">{f.q}</summary>
                <p className="mt-2 text-slate-700 faq-answer">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mt-10 rounded-lg bg-blue-600 p-6 text-white">
          <p className="text-white text-xl font-bold">{copy.ctaCall}</p>
          <p className="text-blue-100">{copy.ctaLine}</p>
        </section>

        <RelatedClusters locale={locale as never} theme="pcc" exclude="pcc" />
        <ContentFreshness
          path={`/${locale}/pcc/services/${service.slug}`}
          locale={locale as never}
        />

      </article>
    </>
  );
}
